import * as React from 'react';
import { useCallback, useMemo } from 'react';
import omitBy from 'lodash.omitby';
import pickBy from 'lodash.pickby';
import { useRouter } from 'next/router';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { BellAlertIcon, ClipboardDocumentListIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import {
  cn,
  formatExplorerOverallFilterData,
  useResponsiveBreakPoints,
  useAnalytics,
  SalaIcon,
  CucinaIcon,
  BarIcon,
  ReceptionIcon,
  useAmbassadorData,
} from '@restworld/utils-common';
import { GroupOptionsType, Button as ButtonV2, Heading } from '@restworld/ui-ds-base';
import { useAuth } from '@restworld/feature-authentication';

import { isRestaurantsExplorerAtom, saveResearchModalV2Atom } from './job-explorer-map';
import JobExplorerMoreFilter from './job-explorer-more-filter';
import { activeJpIdAtom, hasMapViewAtom, jobListParamsAtom } from './job-explorer-list.atoms';
import { getToggleFullScreenAtom, jobCoordinateParamsAtom, setFullScreenAtom } from './job-explorer-map.atoms';
import { filterContextValuesAtom, moreFiltersAtom } from './job-explorer-filters.atoms';
import { domainInterestAtom } from './job-explorer-server.atoms';
import { AmbassadorButton, useShortUrl, JobExplorerQuickFilters } from '../legacy';
interface IFiltersProps {
  isExplorerHomePage?: boolean;
  testId?: string;
}
export enum quickFilterIcons {
  sala = 'SalaIcon',
  cucina = 'CucinaIcon',
  bar = 'BarIcon',
  reception = 'ReceptionIcon',
}
export const domainInterests: GroupOptionsType[] = [
  {
    key: 'sala',
    id: '',
    label: 'Sala',
    icon: quickFilterIcons.sala,
    Icon: SalaIcon,
  },
  {
    key: 'cucina',
    id: '',
    label: 'Cucina',
    icon: quickFilterIcons.cucina,
    Icon: CucinaIcon,
  },
  {
    key: 'bar',
    id: '',
    label: 'Bar',
    icon: quickFilterIcons.bar,
    Icon: BarIcon,
  },
  {
    key: 'reception',
    id: '',
    label: 'Hotellerie',
    icon: quickFilterIcons.reception,
    Icon: ReceptionIcon,
  },
];
export const ExplorerFilters: React.FunctionComponent<IFiltersProps> = ({ isExplorerHomePage, testId }) => {
  const { t } = useTranslation('job-explorer-page');
  const { onQuickFilterChange } = useJobExploreFilters();
  const { user } = useAuth();
  const { isDesktop, isMobile } = useResponsiveBreakPoints();
  const { trackEvent } = useAnalytics();
  const domainInterest = useAtomValue(domainInterestAtom);
  const filterContextValues = useAtomValue(filterContextValuesAtom);
  const [isRestaurantsExplorer] = useAtom(isRestaurantsExplorerAtom);
  const jobListParams = useAtomValue(jobListParamsAtom);
  const setIsSaveResearchModalOpen = useSetAtom(saveResearchModalV2Atom);
  const [hasMapView, mapViewToggler] = useAtom(hasMapViewAtom);
  const toggleFullScreen = useAtomValue(getToggleFullScreenAtom);
  const setFullScreen = useSetAtom(setFullScreenAtom);
  const { loading, shortUrl: ambassadorShortUrl, getShortenURL } = useShortUrl();
  const ambassadorData = useAmbassadorData();

  const activeQuickFilters = useMemo(
    () =>
      isRestaurantsExplorer
        ? []
        : filterContextValues
        ? [...(filterContextValues['domain_interest'] ? filterContextValues['domain_interest'] : [])]
        : [],
    [filterContextValues, isRestaurantsExplorer]
  );

  const quickFilterItems = useMemo(
    () =>
      domainInterests.map((item) => ({
        ...item,
        id: domainInterest?.find(({ key }) => key === item.key)?.id,
      })),
    [domainInterest]
  );

  const mapListToggleBtnCallback = useCallback(() => {
    mapViewToggler((prev) => !prev);
    if (toggleFullScreen) setFullScreen(false);
  }, [mapViewToggler, setFullScreen, toggleFullScreen]);

  const handleSaveResearch = useCallback(() => {
    trackEvent('initiate_save_search', {
      email: user?.email,
      role: user?.type,
      ...jobListParams,
    });
    setIsSaveResearchModalOpen(true);
  }, [jobListParams, setIsSaveResearchModalOpen, trackEvent, user?.email, user?.type]);

  const jobExployerMoreFilter = useMemo(() => <JobExplorerMoreFilter />, []);

  return (
    <div className="items-center justify-between px-4 flex gap-2 md:gap-0">
      <FilterWrapper data-cy={testId} isExplorerHomePage={isExplorerHomePage}>
        <ButtonV2
          variant="tertiary"
          StartIcon={ClipboardDocumentListIcon}
          onClick={mapListToggleBtnCallback}
          className={'flex-1 md:flex-none'}
        >
          {t(hasMapView ? 'list' : 'map')}
        </ButtonV2>
        <div className="h-10 w-[1px] bg-gray-200 hidden md:block" />
        <div className="hidden md:block">{jobExployerMoreFilter}</div>
        {isDesktop && (
          <JobExplorerQuickFilters
            onQuickFilterChange={onQuickFilterChange}
            activeQuickFilters={activeQuickFilters}
            isDisabled={isRestaurantsExplorer}
            quickFilterItems={quickFilterItems}
          />
        )}
        {isRestaurantsExplorer ? <FilterNotAvailableBlurOverlay /> : null}
      </FilterWrapper>
      <div className="flex-1 md:hidden">{jobExployerMoreFilter}</div>
      {/* Two independent questions, deliberately not one ternary any more.
          Share link: shown to anybody with an ambassador record, so a user who
          is both a worker and an ambassador gets it too (they have type
          'worker' so they can still apply, which is why `user.type` is the
          wrong gate here).
          Save-research: a candidate CTA, so it is hidden only from PURE
          ambassadors, who are not candidates. Making it the `else` of the share
          button took it away from dual-role users, who are candidates. */}
      {ambassadorData && (
        <AmbassadorButton
          {...{
            type: ambassadorShortUrl === 'error' ? ('error' as const) : ('success' as const),
            regenerateURL: () => getShortenURL(ambassadorData?.ambassador_code),
            url: ambassadorShortUrl,
            loading,
            // Collapse to icon-only on mobile for dual-role (worker + ambassador) users so the
            // share button doesn't push the save-research CTA off-screen (~390px). Pure
            // ambassadors keep the full label (save-research is hidden for them, so there's room).
            iconOnly: isMobile && user?.type !== 'ambassador',
          }}
          className={'flex-1 max-w-[33%] md:flex-none'}
        />
      )}
      {user?.type !== 'ambassador' && (
        <>
          <ButtonV2
            StartIcon={BellAlertIcon}
            onClick={handleSaveResearch}
            className={'flex-1 max-w-[33%] md:flex-none md:hidden'}
          >
            {t('save')}
          </ButtonV2>
          <ButtonV2
            StartIcon={BellAlertIcon}
            onClick={handleSaveResearch}
            className={'hidden flex-1 max-w-[33%] md:flex-none md:flex'}
          >
            {t('save_search')}
          </ButtonV2>
        </>
      )}
    </div>
  );
};
export default ExplorerFilters;

export const useJobExploreFilters = () => {
  const router = useRouter();
  const setMoreFilters = useSetAtom(moreFiltersAtom);
  const setFilterContextValues = useSetAtom(filterContextValuesAtom);
  const setActiveJpId = useSetAtom(activeJpIdAtom);
  const setJobListParams = useSetAtom(jobListParamsAtom);
  const setJobCoordinateParams = useSetAtom(jobCoordinateParamsAtom);

  const onQuickFilterChange = useCallback(
    async (filterValues: string[]) => {
      try {
        setFilterContextValues((prevValues) => {
          const newValues = {
            ...prevValues,
            ...(filterValues.length ? { domain_interest: filterValues } : { domain_interest: undefined }),
            ...(prevValues.occupation_ids && Object.keys(prevValues.occupation_ids).length
              ? {
                  occupation_ids: Object.keys(prevValues.occupation_ids).reduce((acc, key) => {
                    if (filterValues.includes(key)) acc[key] = newValues.occupation_ids[key];
                    return acc;
                  }, {}),
                }
              : {}),
          };

          const formattedParams = formatExplorerOverallFilterData({ values: newValues });
          setJobListParams((prev) => ({
            ...pickBy(prev, (_, key) => ['lat', 'lon', 'radius', 'offset', 'limit', 'order_by'].includes(key)),
            ...formattedParams,
            ...{ offset: 0 },
          }));
          setJobCoordinateParams(formattedParams);
          setMoreFilters(omitBy(newValues, (value) => value === undefined));
          router.push(
            {
              query: !Object.keys(omitBy(newValues, (value) => value === undefined)).length
                ? omitBy(router.query, (_, key) => key === 'overall_filters')
                : {
                    ...router.query,
                    ...(Object.keys(newValues).length ? { overall_filters: JSON.stringify({ ...newValues }) } : {}),
                  },
            },
            null,
            {
              shallow: true,
            }
          );
          return newValues;
        });
        setActiveJpId('');
      } catch (error) {
        console.error(error, 'Error on quick filter change');
      }
    },
    [router, setActiveJpId, setFilterContextValues, setJobCoordinateParams, setJobListParams, setMoreFilters]
  );

  return {
    onQuickFilterChange,
  };
};
const FilterWrapper = ({
  isExplorerHomePage,
  className,
  ...props
}: { isExplorerHomePage?: boolean } & React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('pt-0 lg:pt-0 flex-1 flex flex-row gap-2 justify-start items-center relative', className)}
    {...props}
  />
);
const FilterNotAvailableBlurOverlay = () => {
  const { isMobile } = useResponsiveBreakPoints();
  const { t } = useTranslation();
  return (
    <div className="absolute w-full h-full backdrop-blur-0.75 flex items-center">
      <div className="px-2 py-1 md:px-4 md:py-2 bg-white rounded-2xl shadow max-w-sm mx-auto flex flex-row gap-2">
        <LockClosedIcon className="w-6 h-6 text-primary-800" />
        <div className="flex flex-col gap-0 md:gap-1">
          <Heading size={isMobile ? 6 : 5} className="leading-normal">
            {`${t('job-explorer-page:we_are_working_on_this')}`}
          </Heading>
        </div>
      </div>
    </div>
  );
};
